/**
 * Explore location drops — public mystery pins unlocked by walking nearby.
 */

import {
  collection,
  deleteDoc,
  doc,
  increment,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
} from 'firebase/firestore';
import { deleteObject, getDownloadURL, ref, uploadBytes } from 'firebase/storage';
import { db, storage } from '../../firebase';
import { distanceMeters, REVEAL_RADIUS_M } from './fogReveal';
import { getEarnCoins } from '../users/earnCatalog';
import {
  awardExploreCreatePoints,
  hasExplorePickupAward,
} from './explorePoints';
import { getUserProfile, USERS_COLLECTION } from '../users/userProfile';

export const EXPLORE_DROPS_COLLECTION = 'exploreDrops';
export const DROP_TYPES = Object.freeze(['photo', 'video', 'journal']);
export const DROP_RANKS = Object.freeze(['common', 'unique', 'epic', 'legendary']);
export const DROP_RANK_DEFAULT = 'common';
export const PICKUP_RADIUS_M = REVEAL_RADIUS_M;

export const PHOTO_MAX_BYTES = 5 * 1024 * 1024;
export const VIDEO_MAX_BYTES = 25 * 1024 * 1024;
/** Max recorded / library video length for explore drops. */
export const VIDEO_MAX_SECONDS = 7;
export const JOURNAL_MAX_CHARS = 1000;
export const CAPTION_MAX_CHARS = 280;

const PHOTO_TYPES = new Set(['image/jpeg', 'image/png', 'image/webp']);
const VIDEO_TYPES = new Set(['video/mp4', 'video/webm']);

/** Legacy Game Maker tiers → current rarity names. */
const LEGACY_DROP_RANKS = Object.freeze({
  bronze: 'unique',
  silver: 'epic',
  gold: 'legendary',
});

/** Map a File MIME to photo/video drop type, or null if unsupported. */
export function dropTypeFromMediaFile(file) {
  const mime = file?.type ? String(file.type) : '';
  if (PHOTO_TYPES.has(mime)) return 'photo';
  if (VIDEO_TYPES.has(mime)) return 'video';
  return null;
}

function assertDropType(type) {
  if (!DROP_TYPES.includes(type)) {
    throw new Error('Choose photo, video, or journal.');
  }
}

export function normalizeDropRank(rank) {
  if (DROP_RANKS.includes(rank)) return rank;
  if (typeof rank === 'string' && LEGACY_DROP_RANKS[rank]) {
    return LEGACY_DROP_RANKS[rank];
  }
  return DROP_RANK_DEFAULT;
}

export function dropRankLabel(rank) {
  const r = normalizeDropRank(rank);
  return r.charAt(0).toUpperCase() + r.slice(1);
}

/** Viewer is friends with drop author (not self). */
export function viewerIsFriendWithAuthor(authorUid, viewerProfile) {
  if (!authorUid || !viewerProfile) return false;
  return (viewerProfile.friendIds || []).includes(authorUid);
}

function extForContentType(contentType, type) {
  if (type === 'photo') {
    if (contentType === 'image/png') return 'png';
    if (contentType === 'image/webp') return 'webp';
    return 'jpg';
  }
  if (contentType === 'video/webm') return 'webm';
  return 'mp4';
}

export function explorePickupsFromProfile(profile) {
  const map = profile?.explorePickups;
  if (!map || typeof map !== 'object' || Array.isArray(map)) return {};
  return map;
}

/**
 * Pickup entry shapes:
 * - legacy `true` → treated as opened (already viewed)
 * - `{ collectedAt, openedAt }` → unopened when openedAt is missing/null
 */
export function isPickupUnopened(entry) {
  if (entry === true) return false;
  if (!entry || typeof entry !== 'object') return false;
  return entry.openedAt == null;
}

export function isDropPickedUp(profile, dropId) {
  return Boolean(explorePickupsFromProfile(profile)[dropId]);
}

/** Picked-up drops not yet opened in Collection (own drops never count). */
export function countUnopenedPickups(profile) {
  const map = explorePickupsFromProfile(profile);
  let n = 0;
  Object.keys(map).forEach((id) => {
    if (isPickupUnopened(map[id])) n += 1;
  });
  return n;
}

/** How many drops this user has collected (any pickup entry). */
export function countExplorePickups(profile) {
  return Object.keys(explorePickupsFromProfile(profile)).length;
}

export function isDropPickupUnopened(profile, dropId) {
  if (!dropId) return false;
  return isPickupUnopened(explorePickupsFromProfile(profile)[dropId]);
}

/** Collected for the Collection tab: picked up by walking, or authored by you. */
export function isDropCollected(drop, { profile, uid } = {}) {
  if (!drop?.id) return false;
  if (uid && drop.authorUid === uid) return true;
  return isDropPickedUp(profile, drop.id);
}

export function canUnlockDrop(drop, { walker, profile, uid } = {}) {
  if (!drop?.id) return false;
  if (uid && drop.authorUid === uid) return true;
  if (isDropPickedUp(profile, drop.id)) return true;
  if (!walker || !Number.isFinite(drop.lng) || !Number.isFinite(drop.lat)) return false;
  return distanceMeters(walker.lng, walker.lat, drop.lng, drop.lat) <= PICKUP_RADIUS_M;
}

export function filterCollectedDrops(drops, { profile, uid } = {}) {
  return (drops || []).filter((drop) => isDropCollected(drop, { profile, uid }));
}

export function normalizeDropDoc(id, data = {}) {
  return {
    id,
    type: DROP_TYPES.includes(data.type) ? data.type : 'journal',
    lng: Number(data.lng),
    lat: Number(data.lat),
    authorUid: data.authorUid || '',
    authorName: data.authorName || '',
    authorPhotoURL: data.authorPhotoURL || null,
    text: typeof data.text === 'string' ? data.text : '',
    mediaUrl: data.mediaUrl || null,
    mediaContentType: data.mediaContentType || null,
    mediaPath: data.mediaPath || null,
    mediaThumbUrl: data.mediaThumbUrl || null,
    mediaThumbPath: data.mediaThumbPath || null,
    source: typeof data.source === 'string' ? data.source : null,
    rank: normalizeDropRank(data.rank),
    createdAt: data.createdAt || null,
    updatedAt: data.updatedAt || null,
  };
}

async function deleteStoragePathQuiet(path) {
  if (!path) return;
  try {
    await deleteObject(ref(storage, path));
  } catch (err) {
    const code = err?.code || '';
    if (code !== 'storage/object-not-found' && code !== 'storage/unauthorized') {
      console.warn('explore drop storage delete failed:', err);
    }
  }
}

async function uploadExploreThumbFile({ uid, dropId, thumbFile }) {
  if (!thumbFile) return { mediaThumbUrl: null, mediaThumbPath: null };
  const mediaThumbPath = `explore-drops/${uid}/${dropId}.thumb.jpg`;
  const storageRef = ref(storage, mediaThumbPath);
  await uploadBytes(storageRef, thumbFile, {
    contentType: thumbFile.type || 'image/jpeg',
  });
  const mediaThumbUrl = await getDownloadURL(storageRef);
  return { mediaThumbUrl, mediaThumbPath };
}

export function subscribeExploreDrops(onChange, onError) {
  const q = query(
    collection(db, EXPLORE_DROPS_COLLECTION),
    orderBy('createdAt', 'desc')
  );
  return onSnapshot(
    q,
    (snap) => {
      const drops = snap.docs.map((d) => normalizeDropDoc(d.id, d.data()));
      onChange(drops);
    },
    (err) => {
      if (onError) onError(err);
    }
  );
}

/**
 * Create a drop at lng/lat. For photo/video, pass a File as `file`.
 * Public identity is anonymous: authorName / authorPhotoURL are not stored.
 * Ownership stays on authorUid for Mine / edit / points.
 */
export async function createExploreDrop({
  uid,
  type,
  lng,
  lat,
  text = '',
  file = null,
  /** Optional JPEG preview for cards; generated client-side via makeExploreMediaThumb. */
  thumbFile = null,
  /** Other drops known to the client; own ones gate the +20 new-place create award. */
  existingDrops = [],
  /** Skip +20 create coins (Game Maker desk seeding). */
  skipCreatePoints = false,
  /** Optional provenance, e.g. 'gameMaker' — not shown to players. */
  source = null,
  /** Cosmetic rarity: common | unique | epic | legendary */
  rank = DROP_RANK_DEFAULT,
}) {
  if (!uid) throw new Error('Sign in to drop something.');
  assertDropType(type);
  if (!Number.isFinite(lng) || !Number.isFinite(lat)) {
    throw new Error('Location required — allow walking location first.');
  }

  const trimmedText = String(text || '').trim();
  if (type === 'journal') {
    if (!trimmedText) throw new Error('Write a journal entry.');
    if (trimmedText.length > JOURNAL_MAX_CHARS) {
      throw new Error(`Journal must be under ${JOURNAL_MAX_CHARS} characters.`);
    }
  } else if (trimmedText.length > CAPTION_MAX_CHARS) {
    throw new Error(`Caption must be under ${CAPTION_MAX_CHARS} characters.`);
  }

  const dropRef = doc(collection(db, EXPLORE_DROPS_COLLECTION));
  let mediaUrl = null;
  let mediaContentType = null;
  let mediaPath = null;
  let mediaThumbUrl = null;
  let mediaThumbPath = null;

  if (type === 'photo' || type === 'video') {
    if (!file) throw new Error(type === 'photo' ? 'Choose a photo.' : 'Choose a video.');
    const max = type === 'photo' ? PHOTO_MAX_BYTES : VIDEO_MAX_BYTES;
    const allowed = type === 'photo' ? PHOTO_TYPES : VIDEO_TYPES;
    if (!allowed.has(file.type)) {
      throw new Error(
        type === 'photo'
          ? 'Photo must be JPEG, PNG, or WebP.'
          : 'Video must be MP4 or WebM.'
      );
    }
    if (file.size > max) {
      throw new Error(
        type === 'photo' ? 'Photo must be under 5MB.' : 'Video must be under 25MB.'
      );
    }
    const ext = extForContentType(file.type, type);
    mediaPath = `explore-drops/${uid}/${dropRef.id}.${ext}`;
    const storageRef = ref(storage, mediaPath);
    await uploadBytes(storageRef, file, { contentType: file.type });
    mediaUrl = await getDownloadURL(storageRef);
    mediaContentType = file.type;

    try {
      const thumb = await uploadExploreThumbFile({
        uid,
        dropId: dropRef.id,
        thumbFile,
      });
      mediaThumbUrl = thumb.mediaThumbUrl;
      mediaThumbPath = thumb.mediaThumbPath;
    } catch (err) {
      console.warn('explore drop thumb upload failed:', err);
    }
  }

  const payload = {
    type,
    lng,
    lat,
    authorUid: uid,
    authorName: '',
    authorPhotoURL: null,
    text: trimmedText,
    mediaUrl,
    mediaContentType,
    mediaPath,
    mediaThumbUrl,
    mediaThumbPath,
    rank: normalizeDropRank(rank),
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  };
  if (source) {
    payload.source = String(source);
  }

  await setDoc(dropRef, payload);
  let pointsEarned = 0;
  if (!skipCreatePoints) {
    const priorOwnDrops = (Array.isArray(existingDrops) ? existingDrops : [])
      .filter(
        (d) =>
          d &&
          d.authorUid === uid &&
          d.id !== dropRef.id &&
          Number.isFinite(d.lng) &&
          Number.isFinite(d.lat)
      )
      .map((d) => ({ lng: d.lng, lat: d.lat }));
    pointsEarned = await awardExploreCreatePoints(uid, dropRef.id, null, {
      lng,
      lat,
      priorOwnDrops,
    });
  }
  return {
    ...normalizeDropDoc(dropRef.id, { ...payload, createdAt: null, updatedAt: null }),
    pointsEarned,
  };
}

/**
 * Author/admin: update text and optionally replace media (same type).
 * Type and author stay immutable. lng/lat only when `allowLocationEdit` (Game Maker).
 */
export async function updateExploreDrop({
  dropId,
  uid,
  type,
  text = '',
  file = null,
  thumbFile = null,
  previousMediaPath = null,
  previousMediaThumbPath = null,
  /** When set, updates cosmetic rank (Game Maker). */
  rank = undefined,
  /** Game Maker only: allow moving the pin. */
  allowLocationEdit = false,
  lng = undefined,
  lat = undefined,
}) {
  if (!dropId) throw new Error('Missing drop id.');
  if (!uid) throw new Error('Sign in to edit this drop.');
  assertDropType(type);

  const trimmedText = String(text || '').trim();
  if (type === 'journal') {
    if (!trimmedText) throw new Error('Write a journal entry.');
    if (trimmedText.length > JOURNAL_MAX_CHARS) {
      throw new Error(`Journal must be under ${JOURNAL_MAX_CHARS} characters.`);
    }
  } else if (trimmedText.length > CAPTION_MAX_CHARS) {
    throw new Error(`Caption must be under ${CAPTION_MAX_CHARS} characters.`);
  }

  const patch = {
    text: trimmedText,
    updatedAt: serverTimestamp(),
  };
  if (rank !== undefined) {
    patch.rank = normalizeDropRank(rank);
  }
  if (allowLocationEdit) {
    const nextLng = Number(lng);
    const nextLat = Number(lat);
    if (!Number.isFinite(nextLng) || !Number.isFinite(nextLat)) {
      throw new Error('Valid location required.');
    }
    patch.lng = nextLng;
    patch.lat = nextLat;
  }

  if (file && (type === 'photo' || type === 'video')) {
    const max = type === 'photo' ? PHOTO_MAX_BYTES : VIDEO_MAX_BYTES;
    const allowed = type === 'photo' ? PHOTO_TYPES : VIDEO_TYPES;
    if (!allowed.has(file.type)) {
      throw new Error(
        type === 'photo'
          ? 'Photo must be JPEG, PNG, or WebP.'
          : 'Video must be MP4 or WebM.'
      );
    }
    if (file.size > max) {
      throw new Error(
        type === 'photo' ? 'Photo must be under 5MB.' : 'Video must be under 25MB.'
      );
    }
    const ext = extForContentType(file.type, type);
    const mediaPath = `explore-drops/${uid}/${dropId}.${ext}`;
    const storageRef = ref(storage, mediaPath);
    await uploadBytes(storageRef, file, { contentType: file.type });
    patch.mediaUrl = await getDownloadURL(storageRef);
    patch.mediaContentType = file.type;
    patch.mediaPath = mediaPath;

    try {
      const thumb = await uploadExploreThumbFile({ uid, dropId, thumbFile });
      patch.mediaThumbUrl = thumb.mediaThumbUrl;
      patch.mediaThumbPath = thumb.mediaThumbPath;
    } catch (err) {
      console.warn('explore drop thumb upload failed:', err);
      patch.mediaThumbUrl = null;
      patch.mediaThumbPath = null;
    }

    const oldPath = previousMediaPath ? String(previousMediaPath) : '';
    if (oldPath && oldPath !== mediaPath) {
      await deleteStoragePathQuiet(oldPath);
    }
    const oldThumb = previousMediaThumbPath ? String(previousMediaThumbPath) : '';
    const nextThumb = patch.mediaThumbPath || '';
    if (oldThumb && oldThumb !== nextThumb) {
      await deleteStoragePathQuiet(oldThumb);
    }
  }

  await updateDoc(doc(db, EXPLORE_DROPS_COLLECTION, dropId), patch);
  return patch;
}

export async function markExploreDropPickedUp(uid, dropId, profile = null) {
  if (!uid || !dropId) throw new Error('Missing user or drop.');
  const existing = profile || (await getUserProfile(uid));
  const patch = {
    [`explorePickups.${dropId}`]: {
      collectedAt: serverTimestamp(),
      openedAt: null,
    },
    updatedAt: serverTimestamp(),
  };
  let pointsEarned = 0;
  if (!hasExplorePickupAward(existing, dropId)) {
    pointsEarned = getEarnCoins().DROP_PICKUP;
    patch[`explorePointAwards.pickups.${dropId}`] = pointsEarned;
    if (pointsEarned > 0) patch.mCoins = increment(pointsEarned);
  }
  await updateDoc(doc(db, USERS_COLLECTION, uid), patch);
  return { pointsEarned };
}

/** Mark a collected pickup as opened (Collection detail). No-op if already opened / missing. */
export async function markExploreDropOpened(uid, dropId, profile) {
  if (!uid || !dropId) throw new Error('Missing user or drop.');
  const entry = explorePickupsFromProfile(profile)[dropId];
  if (!entry || !isPickupUnopened(entry)) return false;
  const collectedAt =
    typeof entry === 'object' && entry.collectedAt != null
      ? entry.collectedAt
      : serverTimestamp();
  await updateDoc(doc(db, USERS_COLLECTION, uid), {
    [`explorePickups.${dropId}`]: {
      collectedAt,
      openedAt: serverTimestamp(),
    },
    updatedAt: serverTimestamp(),
  });
  return true;
}

/** Admin / author: remove drop doc and Storage media when present. */
export async function deleteExploreDrop(drop) {
  const dropId = typeof drop === 'string' ? drop : drop?.id;
  if (!dropId) throw new Error('Missing drop id.');
  const mediaPath =
    typeof drop === 'object' && drop?.mediaPath ? String(drop.mediaPath) : '';
  const mediaThumbPath =
    typeof drop === 'object' && drop?.mediaThumbPath ? String(drop.mediaThumbPath) : '';

  await deleteDoc(doc(db, EXPLORE_DROPS_COLLECTION, dropId));

  await deleteStoragePathQuiet(mediaPath);
  await deleteStoragePathQuiet(mediaThumbPath);
}
